import { SelectionModel } from '@angular/cdk/collections';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroupDirective, UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProjectFileSystemType } from '@common/project-files/enums/project-file-system-type.enum';
import { join } from '@common/util/path';
import { environment } from '@env/environment';
import { AuthUrlService } from '@portal-core/auth/services/auth-url.service';
import { BranchesService } from '@portal-core/branches/services/branches.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '@portal-core/general/components/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent, ErrorDialogData } from '@portal-core/general/components/error-dialog/error-dialog.component';
import { TreeService } from '@portal-core/general/services/tree.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { License } from '@portal-core/licenses/models/license.model';
import { AuditCategory } from '@portal-core/notifications/models/audit-category.model';
import { OAuthVendor } from '@portal-core/oauth/enums/oauth-vendor.enum';
import { OAuthService } from '@portal-core/oauth/services/oauth.service';
import { NonBatchTargetFileFilter } from '@portal-core/project-files/constants/file-filters.constants';
import { ProjectFolder } from '@portal-core/project-files/enums/project-folder.enum';
import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { Project } from '@portal-core/projects/models/project.model';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { Site } from '@portal-core/sites/models/site.model';
import { SitesService } from '@portal-core/sites/services/sites.service';
import { EntityFilterType } from '@portal-core/slack/enums/entity-filter-type.enum';
import { WebhookChannel } from '@portal-core/slack/models/webhook-channel.model';
import { WebhookSubscriber } from '@portal-core/slack/models/webhook-subscriber.model';
import { SlackWebhookChannelsService } from '@portal-core/slack/services/slack-webhooks-channels.service';
import { SlackWebhooksService } from '@portal-core/slack/services/slack-webhooks.service';
import { Task } from '@portal-core/tasks/models/task.model';
import { TasksService } from '@portal-core/tasks/services/tasks.service';
import { Team } from '@portal-core/teams/models/team.model';
import { TeamsService } from '@portal-core/teams/services/teams.service';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { filter as _filter, flatMap as _flatMap } from 'lodash';
import { Observable, Subject, catchError, distinctUntilChanged, filter, forkJoin, map, mergeMap, of, startWith, switchMap, takeUntil } from 'rxjs';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { LoaderComponent } from '@portal-core/general/components/loader/loader.component';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatList, MatListItem, MatSelectionList, MatListOption } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { McColorDirective } from '@portal-core/ui/themes/directives/mc-color.directive';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';

/**
 * The value emitted by the Slack channel-subscription form. Each selection control holds either a
 * chosen entity id or a filter sentinel string (resolved by resolveSelection into an id/filter pair).
 */
interface SlackChannelSubscriptionFormValue {
  name: string;
  channel: WebhookChannel;
  activityType: AuditCategory;
  task: number | string | null;
  project: number | string | null;
  branch: string | null;
  target: string | null;
  team: number | string | null;
  site: number | string | null;
  user: string | null;
}

@Component({
    selector: 'mc-license-slack-form',
    templateUrl: './license-slack-form.component.html',
    styleUrls: ['./license-slack-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, NgIf, LoaderComponent, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatButton, MatList, NgFor, MatListItem, MatIconButton, MatIcon, MatFormField, McColorDirective, MatLabel, MatInput, MatSelect, MatOption, MatSelectionList, MatListOption, AsyncPipe],
})
export class LicenseSlackFormComponent implements OnInit, OnChanges, OnDestroy {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly slackWebhooksService = inject(SlackWebhooksService);
  private readonly slackWebhookChannelsService = inject(SlackWebhookChannelsService);
  private readonly projectsService = inject(ProjectsService);
  private readonly licenseUsersService = inject(LicenseUsersService);
  private readonly teamsService = inject(TeamsService);
  private readonly projectFilesService = inject(ProjectFilesService);
  private readonly branchesService = inject(BranchesService);
  private readonly tasksService = inject(TasksService);
  private readonly sitesService = inject(SitesService);
  private readonly errorService = inject(ErrorService);
  private readonly oAuthService = inject(OAuthService);
  private readonly authUrlService = inject(AuthUrlService);
  private readonly treeService = inject(TreeService);
  private readonly cdr = inject(ChangeDetectorRef);
  @Input() license: License;
  @Input() licenseUser: LicenseUser;
  @Input() subscribers: WebhookSubscriber[];
  @Input() channels: WebhookChannel[];
  @Input() auditCategoryTree: AuditCategory[];
  @Output() canceled: EventEmitter<void> = new EventEmitter<void>();

  editSlackNotificationForm: UntypedFormGroup;
  showEdit: boolean = false;
  projects$: Observable<Project[]>;
  users$: Observable<LicenseUser[]>;
  teams$: Observable<Team[]>;
  branches$: Observable<string[]>;
  targets$: Observable<ProjectFile[]>;
  tasks$: Observable<Task[]>;
  sites$: Observable<Site[]>;
  supportedActivityTypes: string[] = [
    'Builds',
    'Checklists',
    'Projects',
    'Sites',
    'Tasks',
    'Teams',
    'Users'
  ];
  activityTypes: AuditCategory[];
  newWebhookSubscriber: WebhookSubscriber;
  auditCategorySelection: SelectionModel<AuditCategory> = new SelectionModel<AuditCategory>(true);
  awaitingResponse: boolean = false;

  private readonly unsubscribe = new Subject<void>();
  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  ngOnInit() {
    this.editSlackNotificationForm = this.formBuilder.group(this.buildForm());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.auditCategoryTree && changes.auditCategoryTree.currentValue !== null && changes.auditCategoryTree.previousValue !== changes.auditCategoryTree.currentValue) {
      this.activityTypes = this.auditCategoryTree.filter(activity => {
        return activity.Visible && this.supportedActivityTypes.includes(activity.Title);
      });
    }
  }

  buildForm() {
    const group = {
      name: new FormControl('', Validators.required),
      channel: new FormControl('', Validators.required),
      activityType: new FormControl(null, Validators.required),
      task: new FormControl(''),
      project: new FormControl('', Validators.required),
      branch: new FormControl('', Validators.required),
      target: new FormControl('', Validators.required),
      team: new FormControl(''),
      site: new FormControl(''),
      user: new FormControl(null)
    };

    // nested select conditional validation

    group.activityType.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      group.project.reset('');
      group.branch.reset('');
      group.target.reset('');
      if (value?.Title === 'Builds' || value?.Title === 'Checklists' || value?.Title === 'Projects' ||
        (value?.Title === 'Tasks' && group.task?.value === 'allWithProject')) {
        group.project.enable();
      }
      else {
        group.project.disable();
        group.branch.disable();
        group.target.disable();
      }
      group.project.updateValueAndValidity();
      group.branch.updateValueAndValidity();
      group.target.updateValueAndValidity();
    });

    group.project.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      group.branch.reset('');
      group.target.reset('');
      if (group.activityType?.value?.Title === 'Builds' && value !== 'all') {
        group.branch.enable();
      }
      else {
        group.branch.disable();
        group.target.disable();
      }
      group.branch.updateValueAndValidity();
      group.target.updateValueAndValidity();
    });

    group.branch.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      group.target.reset('');
      if (group.branch?.value !== '' && group.branch?.value !== 'all') {
        group.target.enable();
      }
      else {
        group.target.disable();
      }
      group.target.updateValueAndValidity();
    });

    group.task.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
      // prevent task-specific validation from affecting validation for other activity types
      if (group.activityType?.value?.Title !== 'Tasks') {
        return;
      }
      group.project.reset('');
      if (value === 'allWithProject') {
        group.project.enable();
      }
      else {
        group.project.disable();
      }
      group.project.updateValueAndValidity();
    });

    return group;
  }

  addSlackChannel() {
    // For security purposes to authenticate the redirect back from Slack
    const generatedStateValue = this.oAuthService.generateStateParam();

    const slackAuthUrl = 'https://slack.com/oauth/v2/authorize';
    // Permissions our app is requesting (currently just incoming webhook, to send messages to a Slack channel)
    const scopeParam = 'scope=incoming-webhook,channels:read,groups:read';
    // The MadCap Central app id. This is public and safe to share, unlike ClientSecret
    const clientIdParam = `client_id=${environment.slackAppClientId}`;
    // Security param to authenticate the slack request after the redirect
    const stateParam = `state=${generatedStateValue}`;
    // Users will be sent here when returning to Central
    // NOTE: the API has to submit this exact value to Slack when confirming, so if this changes the API must also be updated. See: WebhooksController.PutConfirmAuthChannel
    // TODO: Remove this line that hard codes the confirm slack path prefix to the portal base URI. This is a temporary workaround to make the slack confirmation flow work until Slack accepts our configuration changes
    // NGINX checks for the confirm slack path prefix and redirects to the correct 'confirm' subdomain without the confirm slack path prefix in the path
    const baseRedirectUri = environment.centralSlackRedirectBaseUri ? environment.centralSlackRedirectBaseUri : environment.centralPortalBaseUri;
    const redirectUri = encodeURIComponent(join([baseRedirectUri, 'confirm-slack', this.authUrlService.getSubdomain(), this.license.Id, 'confirm/slack']));
    // const redirectUri = encodeURIComponent(join([this.authUrlService.buildSubdomainUrl(environment.centralConfirmSubdomain), this.authUrlService.getSubdomain(), this.license.Id, 'confirm/slack']));
    const redirectUriParam = `redirect_uri=${redirectUri}`;

    // Store the state id and current route for use after Slack redirects back to Central
    this.oAuthService.setConfirm$(OAuthVendor.Slack, generatedStateValue).subscribe(() => {
      // Leave Central and visit Slack for authorization
      window.location.href = `${slackAuthUrl}?${scopeParam}&${clientIdParam}&${stateParam}&${redirectUriParam}`;
    });
  }

  deleteSlackChannel(channel: WebhookChannel) {
    this.createDeleteConfirmationDialog(`Are you sure you want to delete channel ${channel.Name}?`)
      .afterClosed().pipe(
        filter(confirmed => confirmed),
        switchMap(() => {
          this.awaitingResponse = true;
          this.cdr.markForCheck();
          return this.slackWebhookChannelsService.deleteChannel$(channel.Id);
        })
      ).subscribe(() => {
        this.awaitingResponse = false;
        this.showMessage('Channel deleted');
        this.cdr.markForCheck();
      },
        (errorResponse: HttpErrorResponse) => {
          this.awaitingResponse = false;
          this.displayError('Error Deleting', 'There was a problem deleting the channel.', errorResponse);
          this.cdr.markForCheck();
        }
      );
  }

  editSlackChannelSubscription(subscriber?: WebhookSubscriber) {
    this.showEdit = true;
    this.newWebhookSubscriber = Object.assign({}, subscriber);

    if (subscriber) {
      this.slackWebhooksService.getSubscriptions$(subscriber.Id).subscribe(
        subscriptions => {
          // Store subscribed audit categories
          const selectedCategories = _flatMap(this.activityTypes, p => p.SubCategories.filter(cat => subscriptions.some(sub => sub.AuditCategoryId === cat.Id)));
          this.auditCategorySelection.select(...selectedCategories);
          this.cdr.markForCheck();
        }
      );
      this.editSlackNotificationForm.reset({
        name: subscriber.Name,
        channel: this.channels.find(channel => channel.Id === subscriber.WebhookChannelId),
        activityType: this.activityTypes.find(activity => activity.AuditTargetPageType === subscriber.AuditTargetPageType),
        task: this.toFormSelection(subscriber.TaskId, subscriber.TaskFilter),
        project: this.toFormSelection(subscriber.ProjectId, subscriber.ProjectFilter),
        branch: this.toFormSelection(subscriber.BranchName, subscriber.BranchNameFilter),
        target: this.toFormSelection(subscriber.TargetPath, subscriber.TargetPathFilter),
        team: this.toFormSelection(subscriber.TeamId, subscriber.TeamFilter),
        site: this.toFormSelection(subscriber.SiteId, subscriber.SiteFilter),
        user: this.toFormSelection(subscriber.UserId, subscriber.UserFilter, null),
      });
      this.editSlackNotificationForm.updateValueAndValidity();
    }

    this.populateCategories();
    this.cdr.markForCheck();
  }

  private populateCategories() {
    this.projects$ = this.projectsService.getProjectsList$(this.license.Id).pipe(
      // Only get non archived projects
      map(projects => _filter(projects, project => project.Status !== 0)),
      distinctUntilChanged()
    );
    this.users$ = this.licenseUsersService.getAllLicenseUsersByLicenseId$(this.license.Id, false).pipe(
      // Only get non deactivated users
      map(licenseUsers => _filter(licenseUsers, licenseUser => licenseUser.Status !== 0)),
      distinctUntilChanged()
    );
    this.teams$ = this.teamsService.getTeamsByLicenseId$(this.license.Id).pipe(
      distinctUntilChanged()
    );
    this.tasks$ = this.tasksService.getAllTasks$(this.license.Id).pipe(
      distinctUntilChanged()
    );
    this.sites$ = this.sitesService.getSitesByLicenseId$(this.license.Id).pipe(
      distinctUntilChanged()
    );

    const projectControl = this.editSlackNotificationForm.get('project');
    this.branches$ = projectControl.valueChanges.pipe(
      startWith(projectControl.value),
      switchMap((projectId: number | null) => {
        const activity = this.editSlackNotificationForm.get('activityType')?.value;
        if (activity?.Title === 'Builds' && projectId && Number.isInteger(projectId)) {
          return this.branchesService.getProjectBranches$(projectId);
        }
        return of([]);
      }),
      distinctUntilChanged()
    );

    const branchControl = this.editSlackNotificationForm.get('branch');
    this.targets$ = branchControl.valueChanges.pipe(
      startWith(branchControl.value),
      switchMap((branch: string | null) => {
        if (branch && branch !== 'all') {
          const projectId = projectControl?.value;
          if (projectId && Number.isInteger(projectId)) {
            return this.projectFilesService.getProjectFileTree$(projectId, branch, ProjectFolder.Targets, true, NonBatchTargetFileFilter, true).pipe(
              // a deleted branch returns an error response, which shouldn't terminate the observable
              catchError(() => of([]))
            );
          }
        }
        return of([]);
      }),
      map(items => _flatMap(items, item => this.treeService.flatten(item, 'Items'))
        .filter(file => file.Type === ProjectFileSystemType.File)),
      distinctUntilChanged()
    );
  }

  deleteSlackSubscriber(subscriber: WebhookSubscriber) {
    this.createDeleteConfirmationDialog(`Are you sure you want to delete notification ${subscriber.Name}?`)
      .afterClosed().pipe(
        filter(confirmed => confirmed),
        switchMap(() => {
          this.awaitingResponse = true;
          this.cdr.markForCheck();
          return this.slackWebhooksService.deleteSubscriber$(subscriber.Id);
        })
      ).subscribe(() => {
        this.awaitingResponse = false;
        this.showMessage('Notification subscription deleted');
        this.cdr.markForCheck();
      },
        (errorResponse: HttpErrorResponse) => {
          this.awaitingResponse = false;
          this.displayError('Error Deleting', 'There was a problem deleting the notification subscription.', errorResponse);
          this.cdr.markForCheck();
        }
      );
  }

  /**
   * Maps a form-select value (an entity ID, an "all"/"allWith*" sentinel, or empty)
   * to the pair of fields the WebhookSubscriber model expects: the singleton id and the filter enum.
   */
  private resolveSelection<T extends number | string>(formValue: T | string | null): { id: T | null; filter: EntityFilterType } {
    switch (formValue) {
      case null:
      case undefined:
      case '':
        return { id: null, filter: EntityFilterType.NoFilter };
      case 'all':
        return { id: null, filter: EntityFilterType.All };
      case 'allWithoutProject':
        return { id: null, filter: EntityFilterType.AllWithNoProject };
      case 'allWithProject':
        return { id: null, filter: EntityFilterType.AllWithSingletonProject };
      default:
        return { id: formValue as T, filter: EntityFilterType.Singleton };
    }
  }

  /**
   * Inverse of [[resolveSelection]]: maps a (singleton id, filter enum) pair from a WebhookSubscriber
   * back to the form-select value — either the id itself, an "all"/"allWith*" sentinel, or the
   * caller-supplied empty value (`''` for most fields, `null` for the user select).
   */
  private toFormSelection<T extends number | string>(id: T | null | undefined, entityFilter: EntityFilterType, emptyValue: T | string | null = ''): T | string | null {
    if (id) {
      return id;
    }
    switch (entityFilter) {
      case EntityFilterType.All:
        return 'all';
      case EntityFilterType.AllWithNoProject:
        return 'allWithoutProject';
      case EntityFilterType.AllWithSingletonProject:
        return 'allWithProject';
      default:
        return emptyValue;
    }
  }

  saveChannelSubscription(values: SlackChannelSubscriptionFormValue, formGroupDirective: FormGroupDirective) {
    const task = this.resolveSelection<number>(values.task);
    const project = this.resolveSelection<number>(values.project);
    const branch = this.resolveSelection<string>(values.branch);
    const target = this.resolveSelection<string>(values.target);
    const team = this.resolveSelection<number>(values.team);
    const site = this.resolveSelection<number>(values.site);
    const user = this.resolveSelection<string>(values.user);

    this.newWebhookSubscriber = Object.assign(this.newWebhookSubscriber, {
      Name: values.name,
      LicenseId: this.license.Id,
      WebhookChannelId: values.channel.Id,
      WebhookChannelName: values.channel.Name,
      AuditTargetPageType: values.activityType.AuditTargetPageType,
      TaskId: task.id,
      TaskFilter: task.filter,
      ProjectId: project.id,
      ProjectFilter: project.filter,
      BranchName: branch.id,
      BranchNameFilter: branch.filter,
      TargetPath: target.id,
      TargetPathFilter: target.filter,
      TeamId: team.id,
      TeamFilter: team.filter,
      SiteId: site.id,
      SiteFilter: site.filter,
      UserId: user.id,
      UserFilter: user.filter,
    });

    const request$ = this.newWebhookSubscriber.Id ? this.slackWebhooksService.updateSubscriber$(this.newWebhookSubscriber) :
      this.slackWebhooksService.addSubscriber$(this.newWebhookSubscriber);
    this.awaitingResponse = true;
    request$.pipe(
      mergeMap((dBsubscriber: WebhookSubscriber) => {
        // Use server returned subscriber with Id
        this.newWebhookSubscriber.Id = dBsubscriber.Id;
        // Clear all old subscriptions
        return this.slackWebhooksService.clearAllSubscriptions$(this.newWebhookSubscriber.Id);
      }),
      mergeMap(() => {
        // Get subscriptions from the form
        const addSubscriptions$ = this.auditCategorySelection.selected
          .filter(category => category.AuditTargetPageType === values.activityType.AuditTargetPageType)
          .map(category => this.slackWebhooksService.addSubscription$(this.newWebhookSubscriber.Id, category.Id));

        if (addSubscriptions$.length === 0) {
          return of(null);
        }
        // Update subscriber with new subscriptions
        return forkJoin(addSubscriptions$);
      })).subscribe(() => {
        this.awaitingResponse = false;
        this.resetForm(formGroupDirective);
        this.showMessage('Notification subscription saved');
        this.cdr.markForCheck();
      },
        (errorResponse: HttpErrorResponse) => {
          this.awaitingResponse = false;
          this.displayError('Error Saving', 'There was a problem saving the notification subscription.', errorResponse);
          this.cdr.markForCheck();
        }
      );
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', {
      duration: SnackBarAutoDismissDurationMS
    });
  }

  private displayError(title: string, message: string, errorResponse: HttpErrorResponse) {
    this.dialog.open<ErrorDialogComponent, ErrorDialogData, boolean>(ErrorDialogComponent, {
      ...ErrorDialogComponent.DialogConfig,
      data: {
        title: title,
        message: message,
        errors: this.errorService.getErrorMessages(errorResponse)
      }
    });
  }

  getTargetDisplayPath(target: ProjectFile): string {
    const targetsRootFolder = `${ProjectFolder.Targets}/`;
    const extension = '.fltar';
    let path = target.Path ?? '';
    if (path.startsWith(targetsRootFolder)) {
      path = path.substring(targetsRootFolder.length);
    }
    if (path.endsWith(extension)) {
      path = path.slice(0, -extension.length);
    }
    return path;
  }

  toggleCategory(category: AuditCategory) {
    this.auditCategorySelection.toggle(category);
    this.editSlackNotificationForm.markAsDirty();
  }

  resetForm(formGroupDirective: FormGroupDirective) {
    this.showEdit = false;
    formGroupDirective.resetForm();
    this.auditCategorySelection.clear();
    this.newWebhookSubscriber = null;
  }

  private createDeleteConfirmationDialog(prompt: string): MatDialogRef<ConfirmDialogComponent, boolean> {
    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
      width: '36rem',
      data: {
        action: 'Delete',
        title: 'Delete Confirmation',
        prompt: prompt
      }
    });
    return dialogRef;
  }
}
